import React, { FC, useMemo } from 'react';
import { Box, keyframes } from '@mui/material';
import { AutumnLeaf } from './AutumnLeaf';
import { AutumnColors } from './autumnColors';

const LEAF_COLORS = [
  AutumnColors.LEAF_RED,
  AutumnColors.LEAF_ORANGE,
  AutumnColors.LEAF_YELLOW,
  AutumnColors.LEAF_BROWN,
  AutumnColors.PUMPKIN,
];

const fall = keyframes`
  0%   { transform: translate3d(0, -10%, 0) rotate(0deg); opacity: 0; }
  10%  { opacity: 1; }
  50%  { transform: translate3d(var(--sway), 50vh, 0) rotate(180deg); }
  90%  { opacity: 1; }
  100% { transform: translate3d(calc(var(--sway) * -1), 105vh, 0) rotate(360deg); opacity: 0; }
`;

export interface FallingLeavesProps {
  /** Number of leaves. */
  count?: number;
  /** Seconds a leaf takes to fall. Each leaf gets a random duration between min and max. */
  minDuration?: number;
  maxDuration?: number;
  /** Max leaf size in px. */
  maxSize?: number;
  /** Covers the viewport (`fixed`) or just the parent (`absolute`). */
  position?: 'fixed' | 'absolute';
  zIndex?: number;
  /** Loop forever (loader) or fall once (easter egg). */
  loop?: boolean;
}

/** Decorative, non-interactive falling leaves. Respects `prefers-reduced-motion`. */
export const FallingLeaves: FC<FallingLeavesProps> = ({
  count = 30,
  minDuration = 5,
  maxDuration = 9,
  maxSize = 26,
  position = 'fixed',
  zIndex = 1500,
  loop = false,
}) => {
  const leaves = useMemo(
    () =>
      Array.from({ length: count }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * (loop ? maxDuration : 2.5),
        duration: minDuration + Math.random() * (maxDuration - minDuration),
        size: Math.round(maxSize * (0.5 + Math.random() * 0.5)),
        sway: Math.round((Math.random() - 0.5) * 160),
        color: LEAF_COLORS[i % LEAF_COLORS.length],
      })),
    [count, loop, maxDuration, minDuration, maxSize],
  );

  return (
    <Box
      aria-hidden
      data-testid='FallingLeaves'
      sx={{
        position,
        inset: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex,
        '@media (prefers-reduced-motion: reduce)': { display: 'none' },
      }}>
      {leaves.map((leaf) => (
        // The keyframes must be referenced from `sx` (Emotion) to be injected; inline `style` can't resolve them.
        // Per-leaf values go in `style` so all leaves share one generated class.
        <Box
          key={leaf.id}
          data-testid='FallingLeaf'
          sx={{
            position: 'absolute',
            top: 0,
            lineHeight: 0,
            animation: `${fall} linear ${loop ? 'infinite' : '1'} both`,
            willChange: 'transform, opacity',
          }}
          style={
            {
              left: `${leaf.left}%`,
              '--sway': `${leaf.sway}px`,
              animationDuration: `${leaf.duration}s`,
              animationDelay: `${leaf.delay}s`,
            } as React.CSSProperties
          }>
          <AutumnLeaf size={leaf.size} color={leaf.color} />
        </Box>
      ))}
    </Box>
  );
};

export default FallingLeaves;
